import React, { useMemo, useState } from 'react';
import { Modal, Tabs } from 'antd';
import Chart from 'react-google-charts';
import moment from 'moment';
import { IWeatherSnapshot, IGrowthStageData } from '../../Map';
import { INitrogenInsightsResponse } from '../../../Models/Responses/NitrogenInsightsResponse';
import { convertCelciusToFahrenheit, convertMillimetersToInches, convertNullableMillimetersToInches, getMeanFarenheitTemperatureFromCelsius } from '../../../Utility/UnitConversion';
import { EventWeatherTags } from '../../../../tracing/EventTagNames';
import { Checkbox } from '../../../../components/Checkbox/Checkbox';
import * as Styles from './Styles/WeatherDataModalStyles';
import { NitrogenInsightsTabContent } from './NitrogenInsights/NitrogenInsightsTabContent';

type SoilTexture = 'Fine' | 'Medium' | 'Coarse';

interface IWeatherDataModalProps
{
	isOpen: boolean;
	onClose: () => void;
	weatherData: IWeatherSnapshot[];
	growthStageData: IGrowthStageData;
	soilTexture: SoilTexture;
	growthStageByDate: Record<string, string>;
	viewEvent: (tags: Record<string, any>) => void;
	crop?: string;
	nitrogenInsightsData?: INitrogenInsightsResponse;
	nitrogenInsightsError?: string;
	onNitrogenInputsSelected: (relativeMaturity: string, plantingDate: string) => void;
}

export const WeatherDataModal = (props: IWeatherDataModalProps) =>
{
	const { isOpen, onClose, weatherData, growthStageData, soilTexture, growthStageByDate, viewEvent, crop, nitrogenInsightsData, nitrogenInsightsError, onNitrogenInputsSelected } = props;

	if (!weatherData?.length)
	{
		return null;
	}

	return (
		<Modal
			style={Styles.modalPositionStyle}
			bodyStyle={Styles.modalBodyStyle}
			width={'90%'}
			open={isOpen}
			onOk={onClose}
			onCancel={onClose}
		>
			<Tabs 
				destroyInactiveTabPane
				onChange={(activeKey) =>
				{
					viewEvent({
						[EventWeatherTags.ViewTab]: activeKey
					});
				}}
				items={[
					{ 
						label: 'Risk', 
						key: 'risk', 
						children: (
							<RiskChartTabContent
								crop={crop}
								soilTexture={soilTexture} 
								weatherData={weatherData}
								growthStageData={growthStageData}
								growthStageByDate={growthStageByDate}
							/>
						)
					},
					{ 
						label: 'Temperature', 
						key: 'temp', 
						children: (
							<Chart 
								chartType="LineChart"
								options={{
									series: [
										{ color: Styles.CHART_COLORS.blue },
										{ color: Styles.CHART_COLORS.orange },
										{ color: Styles.CHART_COLORS.gray },
									],
									intervals: { 'color': Styles.CHART_COLORS.orange, style: 'area', 'label': '20 yr avg' },	
									interval: {
										'i0': { 'style': 'area', 'curveType':'function', 'fillOpacity':0.3 }	
									},
									hAxis: { title: 'Date' },
									vAxes: {
										0: {logScale: false, title: 'Fahrenheit'},
									},
									backgroundColor: Styles.CHART_COLORS.backgroundColor
								}}
								data={[
									[
										{ type: 'string', label: 'Date' },
										{ type: 'number', label: 'Minimum' },
										{ type: 'number', label: 'Maximum' },
										{ type: 'number', label: 'Mean' },
										{ id: 'i0', type: 'number', label: '20 yr avg', role:'interval' },
										{ id: 'i1', type: 'number', label: '20 yr avg', role:'interval' },
										{ type: 'string', role: 'annotation'}
									],
									...weatherData.filter(wx => wx.CropRiskFineSoil).map(wx => 
									{
										return [
											moment.parseZone(wx.Datestamp).local().format('MMM DD'), 
											convertCelciusToFahrenheit(wx.MinimumTemperature2mCelsius),
											convertCelciusToFahrenheit(wx.MaximumTemperature2mCelsius),
											getMeanFarenheitTemperatureFromCelsius(wx.MinimumTemperature2mCelsius, wx.MaximumTemperature2mCelsius),
											wx.Climatology?.MinimumTemperatureCelsius === undefined ? wx.Climatology?.MinimumTemperatureCelsius : convertCelciusToFahrenheit(wx.Climatology?.MinimumTemperatureCelsius),
											wx.Climatology?.MaximumTemperatureCelsius === undefined ? wx.Climatology?.MaximumTemperatureCelsius : convertCelciusToFahrenheit(wx.Climatology?.MaximumTemperatureCelsius),
											growthStageByDate[new Date(wx.Datestamp).toDateString()]
										];
									})
								]}
								width='100%'
								height='70vh'
							/>
						) 
					},
					{ 
						label: 'Precipitation', 
						key: 'precip', 
						children: (
							<Chart 
								chartType="LineChart"
								options={{
									series: [
										{ color: Styles.CHART_COLORS.blue, type: 'area' },
										{ color: Styles.CHART_COLORS.darkBlue, type: 'area' },
									],
									intervals: { style: 'area' },	
									hAxis: { title: 'Date' },
									vAxes: {
										0: {logScale: false, title: 'Inches'},
									},
									backgroundColor: Styles.CHART_COLORS.backgroundColor
								}}
								data={[
									[
										{ type: 'string', label: 'Date' },
										{ type: 'number', label: '20 yr avg'},
										{ type: 'number', label: 'Current' },
										{ type: 'string', role: 'annotation'}
									],
									...weatherData.filter(wx => wx.CropRiskFineSoil).map(wx => 
									{
										return [
											moment.parseZone(wx.Datestamp).local().format('MMM DD'),
											convertMillimetersToInches(wx.Climatology?.PrecipitationMillimeters), 
											convertMillimetersToInches(wx.PrecipitationMillimeters),
											growthStageByDate[new Date(wx.Datestamp).toDateString()]
										];
									})
								]}
								width='100%'
								height='70vh'
							/>
						) 
					},
					{ 
						label: 'GDDs', 
						key: 'gdd', 
						children: (
							<Chart 
								chartType="LineChart"
								options={{
									series: [
										{ color: Styles.CHART_COLORS.lightGray },
										{ color: Styles.CHART_COLORS.orange },
									],
									intervals: { style: 'area' },	
									hAxis: { title: 'Date' },
									vAxes: {
										0: {logScale: false, title: 'Growing Degree Units'},
									},
									backgroundColor: Styles.CHART_COLORS.backgroundColor
								}}
								data={[
									[
										{ type: 'string', label: 'Date' },
										{ type: 'number', label: '20 yr avg'},
										{ type: 'number', label: 'Current' },
										{ type: 'string', role: 'annotation'}
									],
									...weatherData.filter(wx => wx.CropRiskFineSoil).reduce((prev, wx) => 
									{
										const last = prev.length > 0
											? prev[prev.length-1] 
											: ['', 0, 0];

										return [
											...prev, 
											[
												moment.parseZone(wx.Datestamp).local().format('MMM DD'),
												(last[1] as number) + (wx.Climatology?.CornGrowingDegreeUnits ?? 0), 
												(last[2] as number) + wx.CornGrowingDegreeUnits,
												growthStageByDate[new Date(wx.Datestamp).toDateString()]
											]
										];
									}, [] as any[][])
								]}
								width='100%'
								height='70vh'
							/>
						) 
					},
					{ 
						label: 'Combined', 
						key: 'combined', 
						children: (
							<div style={Styles.combinedChartContainerStyle}>
								<Chart 
									chartType="LineChart"
									options={{
										series: [
											{ color: Styles.CHART_COLORS.gray, type: 'bars'},
											{ color: Styles.CHART_COLORS.lightBlue, type: 'area', targetAxisIndex: 1, trendlineOpacity:0.2, areaOpacity: 0.2 },
											{ color: Styles.CHART_COLORS.lightRed, type: 'lines', targetAxisIndex: 1, trendlineOpacity: 0.2 },
											{ color: Styles.CHART_COLORS.blue, lineWidth: 3 },
											{ color: Styles.CHART_COLORS.orange, lineWidth: 3 },
										],
										intervals: { 'color': Styles.CHART_COLORS.orange, style: 'area', 'label': '20 yr avg' },	
										interval: {
											'i0': { 'style': 'area', 'curveType':'function', 'fillOpacity':0.3 }	
										},
										hAxis: { title: 'Date' },
										vAxes: {
											0: {logScale: false, title: 'Crop Stress Index', minValue:-100, maxValue:100, textPosition: 'none'},
											1: {minValue:0, maxValue:30, title: 'Weather'},
										},
										backgroundColor: Styles.CHART_COLORS.backgroundColor
									}}
									data={[
										[
											{ type: 'string', label: 'Date' },
											{ type: 'string', role: 'annotation'},
											{ type: 'number', label: 'Wind Gust Risk' },
											{ type: 'number', label: 'Precip' },
											{ type: 'number', label: 'Temperature' },
											{ id: 'i0', type: 'number',  role:'interval' },
											{ id: 'i1', type: 'number', role:'interval' },
											{ type: 'number', label: 'Fine Soil' },
											{ type: 'number', label: 'Coarse Soil' },
										],
										...weatherData.filter(wx => wx.CropRiskFineSoil).map(wx => 
										{
											return [
												moment.parseZone(wx.Datestamp).local().format('MMM DD'), 		
												growthStageByDate[new Date(wx.Datestamp).toDateString()],
												wx.WindGustRisk > 0 ? wx.WindGustRisk : undefined,
												convertMillimetersToInches(wx.PrecipitationMillimeters),
												getMeanFarenheitTemperatureFromCelsius(wx.MinimumTemperature2mCelsius, wx.MaximumTemperature2mCelsius),
												convertCelciusToFahrenheit(wx.MinimumTemperature2mCelsius),
												convertCelciusToFahrenheit(wx.MaximumTemperature2mCelsius),
												wx.CropRiskFineSoil, 
												wx.CropRiskCoarseSoil, 									
											];
										})
									]}
									width='100%'
									height='70vh'
								/>
								<div style={Styles.combinedHotDryLabelStyle}>Hot & Dry</div>
								<div style={Styles.combinedMildLabelStyle}>Mild</div>
								<div style={Styles.combinedColdWetLabelStyle}>Cold & Wet</div>
							</div>
						)
					},
					{
						label: 'Nitrogen Insights',
						key: 'nitrogen-insights',
						children: (
							<NitrogenInsightsTabContent
								data={nitrogenInsightsData}
								error={nitrogenInsightsError}
								growthStageByDate={growthStageByDate}
								onNitrogenInputsSelected={onNitrogenInputsSelected}
							/>
						)
					}
				]} 
			/>
		</Modal>
	);
};

const growthStagesOrder = ['Germination', 'VE', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6', 'V7', 'V8', 'V9', 'V10', 'V11', 'V12', 'V13', 'V14', 'VT', 'R1', 'R2', 'R3', 'R4', 'R5', 'R6'];

type GrowthStageType = keyof IGrowthStageData['GrowthStages']

const getGrowthStageDate = (
	growthStages: IGrowthStageData['GrowthStages'], 
	stage: GrowthStageType
): string | undefined => 
{
	const startIndex = growthStagesOrder.indexOf(stage);
	if (startIndex === -1) return undefined; 

	for(let i = startIndex; i < growthStagesOrder.length; i++)
	{
		const growthStageDate: string | undefined = growthStages[growthStagesOrder[i]];
		
		if(growthStageDate) return growthStageDate;
	}

	return undefined;
};

const filterWindGustRiskData = (
	windRisk: number, 
	dataDate: string, 
	displayAll: boolean, 
	isCorn: boolean, 
	cornRiskStartDate: string | undefined
) => 
{
	if (windRisk <= 0) return undefined;

	if (!displayAll) 
	{
		if(!isCorn) return undefined;

		if(!cornRiskStartDate) return undefined;
		
		if(dataDate < cornRiskStartDate) return undefined;
	}

	return windRisk;
};

const getWindGustRiskLabel = (displayAll: boolean, isCorn: boolean, hasStartDate: boolean) => 
{
	if (!displayAll)
	{
		if (isCorn && hasStartDate) return 'Wind Gust Risk\n(After V5)';

		return 'Wind Gust Risk\n(Hidden)';
	}

	return 'Wind Gust Risk';
};

type RiskChartTabContentProps = 
	Readonly<Pick<IWeatherDataModalProps, 'crop' | 'soilTexture' | 'weatherData'|'growthStageData' | 'growthStageByDate'>>;

const RiskChartTabContent = (props: RiskChartTabContentProps) =>
{
	const { crop, soilTexture, weatherData, growthStageData, growthStageByDate } = props;

	const isCorn = crop?.toLowerCase() === 'corn';
	const v5StageDate = getGrowthStageDate(growthStageData?.GrowthStages, 'V5');
	
	// By default, display all wind gust risks when there is no growth stage data
	const [displayAllWindGustRisk, setDisplayAllWindGustRisk] = 
		useState(isCorn && !v5StageDate);

	const chartData = useMemo(() => 
	{
		return [
			[
				{ type: 'string', label: 'Date' },
				{ type: 'string', role: 'annotation' },
				{ type: 'number', label: 'Fine Soil (VLOW)' },
				{ id: 'i4', type: 'number', label: 'VLOW', role: 'interval' },
				{ id: 'i4', type: 'number', label: 'VLOW', role: 'interval' },
				{ type: 'number', label: 'Fine Soil (LOW)' },
				{ id: 'i3', type: 'number', label: 'VLOW', role: 'interval' },
				{ id: 'i3', type: 'number', label: 'VLOW', role: 'interval' },
				{ type: 'number', label: 'Fine Soil (MED)' },
				{ id: 'i2', type: 'number', label: 'VLOW', role: 'interval' },
				{ id: 'i2', type: 'number', label: 'VLOW', role: 'interval' },
				{ type: 'number', label: 'Fine Soil (MED)' },
				{ id: 'i1', type: 'number', label: 'VLOW', role: 'interval' },
				{ id: 'i1', type: 'number', label: 'VLOW', role: 'interval' },
				{ type: 'number', label: 'Fine Soil (VLOW)' },
				{ id: 'i0', type: 'number', label: 'VLOW', role: 'interval' },
				{ id: 'i0', type: 'number', label: 'VLOW', role: 'interval' },

				{ type: 'number', label: `${soilTexture ?? 'Fine'} Soil` },
				{ id: 'i5', type: 'number', label: 'SPAN', role: 'interval' },
				{ id: 'i5', type: 'number', label: 'SPAN', role: 'interval' },

				{ 
					type: 'number', 
					label: getWindGustRiskLabel(
						displayAllWindGustRisk, 	
						isCorn, 
						!!v5StageDate
					)
				},
			],

			...weatherData.filter(wx => wx.CropRiskFineSoil)
				.map(wx => 
				{
					const dateStamp = new Date(wx.Datestamp).toDateString();
					const growthStage = growthStageByDate[dateStamp];

					const windGustRiskData = 
						filterWindGustRiskData(
							wx.WindGustRisk,
							wx.Datestamp,
							displayAllWindGustRisk, 
							isCorn,
							v5StageDate
						);

					const chartDataRow = [
						moment.parseZone(wx.Datestamp).local().format('MMM DD'),
						growthStage,
						wx.CropRiskFineSoil,
						-100,
						-60,
						wx.CropRiskFineSoil,
						-60,
						-20,
						wx.CropRiskFineSoil,
						-20,
						20,
						wx.CropRiskFineSoil,
						20,
						60,
						wx.CropRiskFineSoil,
						60,
						100,
						soilTexture === 'Coarse' ? wx.CropRiskCoarseSoil
							: soilTexture === 'Medium' ? wx.CropRiskMediumSoil
								: wx.CropRiskFineSoil,
						Math.min(wx.CropRiskCoarseSoil, wx.CropRiskMediumSoil, wx.CropRiskFineSoil),
						Math.max(wx.CropRiskCoarseSoil, wx.CropRiskMediumSoil, wx.CropRiskFineSoil),
						windGustRiskData
					];

					return chartDataRow;
				}),
		];
	}, 
	[isCorn, soilTexture, weatherData, growthStageData, growthStageByDate, displayAllWindGustRisk]);

	return (
		<div style={Styles.relativeChartContainerStyle}>
			<Checkbox 
				name="filter-wind-gust-risk" 
				id="filter-wind-gust-risk" 	
				style={Styles.riskChartFilterStyle}
				checked={displayAllWindGustRisk} 
				onChange={() => setDisplayAllWindGustRisk(state => !state)}
			>
					Display all Wind Gust Risks
			</Checkbox>
			<Chart
				chartType="LineChart"
				options={{
					series: [
						{ color: Styles.CHART_COLORS.transparent, visibleInLegend: false },
						{ color: Styles.CHART_COLORS.transparent, visibleInLegend: false },
						{ color: Styles.CHART_COLORS.transparent, visibleInLegend: false },
						{ color: Styles.CHART_COLORS.transparent, visibleInLegend: false },
						{ color: Styles.CHART_COLORS.transparent, visibleInLegend: false },
						{ color: Styles.CHART_COLORS.tan },
						{ color: Styles.CHART_COLORS.darkGray, type: 'bars' },
					],
					intervals: { color: Styles.CHART_COLORS.intervalBlue },
					interval: {
						'i0': { 'style': 'area', 'fillOpacity': 0.20, color: Styles.CHART_COLORS.red },
						'i1': { 'style': 'area', 'fillOpacity': 0.20, color: Styles.CHART_COLORS.yellow },
						'i2': { 'style': 'area', 'fillOpacity': 0.20, color: Styles.CHART_COLORS.green },
						'i3': { 'style': 'area', 'fillOpacity': 0.20, color: Styles.CHART_COLORS.skyBlue },
						'i4': { 'style': 'area', 'fillOpacity': 0.20, color: Styles.CHART_COLORS.navy },
						'i5': { 'style': 'area', 'fillOpacity': 0.25, color: Styles.CHART_COLORS.tan },
					},
					hAxis: { title: 'Date' },
					vAxes: {
						0: { logScale: false, title: 'Crop Stress Index', textPosition: 'none' },
					},
					backgroundColor: Styles.CHART_COLORS.backgroundColor
				}}
				data={chartData}
				width='100%'
				height='70vh'
			/>
			<div style={Styles.riskChartLabelsStyle}>
				<div style={Styles.rotatedChartLabelStyle}>Hot & Dry</div>
				<div style={Styles.rotatedChartLabelStyle}>Mild</div>
				<div style={Styles.rotatedChartLabelStyle}>Cold & Wet</div>
			</div>
		</div>
	);
};
